/**
 * A toolbar dropdown: a summary button that opens a panel under it.
 *
 * It exists so the dashboard toolbar can offer the *same* controls Explore uses
 * - `<date-range>`, `<ship-multi-select>` - without their sidebar footprint. A
 * date picker that is seven chips and two datetime inputs owns a whole toolbar
 * row; behind a button that reads "Last 7 days" it owns one control's width, and
 * the components inside are untouched, so Explore is unaffected and the two
 * cannot drift.
 *
 * Components render into light DOM (see `lit-base.ts`), so `<slot>` does not
 * project. The panel arrives as a `TemplateResult` property instead of as
 * children, which is also what lets the caller keep the panel's contents lazy -
 * nothing inside is rendered until the dropdown is opened.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';

@customElement('dash-dropdown')
export class DashDropdown extends LightElement {
  /** The summary shown on the button - what is currently selected. */
  @property({ type: String }) label = '';
  /** Small leading word naming the control, e.g. "Time" or "Ships". */
  @property({ type: String }) caption = '';
  /** Rendered only while open, so the panel costs nothing when closed. */
  @property({ attribute: false }) panel: () => TemplateResult = () => html``;
  /** Extra class on the panel, for per-dropdown width. */
  @property({ type: String }) panelClass = '';
  /** Right-align the panel with the trigger - for a dropdown near the edge. */
  @property({ type: Boolean }) alignEnd = false;
  /** Extra class on the trigger, e.g. `is-primary` for a menu button. */
  @property({ type: String }) triggerClass = '';
  /**
   * Replaces the caption / label / caret with custom content - an icon-only
   * trigger such as the settings gear. Give it a `triggerTitle` so it is named.
   */
  @property({ attribute: false }) triggerContent: TemplateResult | null = null;
  /** Tooltip and accessible name for the trigger. */
  @property({ type: String }) triggerTitle = '';
  /** ARIA role of the panel: a picker is a `dialog`, a list of actions a `menu`. */
  @property({ type: String }) panelRole: 'dialog' | 'menu' = 'dialog';
  /**
   * Take the panel out of the flow and place it against the viewport.
   *
   * For a dropdown that lives inside something that scrolls - a row in the
   * dashboard list - because an absolutely positioned panel is clipped by any
   * scrolling ancestor, however high its `z-index`. Kept opt-in: the toolbar's
   * dropdowns are not inside a scroll box and stay anchored the simple way.
   */
  @property({ type: Boolean }) fixed = false;

  @state() private open = false;

  private onDocumentPointerDown = (event: Event): void => {
    // The panel is a child of this element (light DOM), so a click anywhere
    // inside it - including the native datetime picker's own input - is still
    // inside `this` and must not close the dropdown.
    if (!this.open || event.composedPath().includes(this)) return;
    this.open = false;
  };

  private onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.open) return;
    this.open = false;
    this.trigger?.focus();
  };

  private get trigger(): HTMLButtonElement | null {
    return this.querySelector<HTMLButtonElement>('.dash-dd-trigger');
  }

  /** A panel's content asking to be put away, e.g. a single-ship pick. */
  private onRequestClose = (event: Event): void => {
    event.stopPropagation();
    this.open = false;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('request-close', this.onRequestClose);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  override disconnectedCallback(): void {
    this.removeEventListener('request-close', this.onRequestClose);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
    this.unwatchViewport();
    super.disconnectedCallback();
  }

  /* ── Fixed placement ──────────────────────────────────── */

  private onViewportChange = (): void => {
    // Follow the trigger rather than closing. Closing on any scroll sounds
    // tidy and is not: scroll fires in the capture phase for every scrollable
    // thing on the page, including ones the user never touched, and the menu
    // would vanish the moment anything else moved.
    if (this.open) this.placePanel();
  };

  private watchViewport(): void {
    window.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
  }

  private unwatchViewport(): void {
    window.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
  }

  /** Place the panel under the trigger, in viewport coordinates. */
  private placePanel(): void {
    const panel = this.querySelector<HTMLElement>('.dash-dd-panel');
    const trigger = this.trigger;
    if (!panel || !trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const gap = 4;
    // Right-aligned with the trigger by default here, since these sit at the
    // right edge of a row; flipped above when there is no room below.
    const left = this.alignEnd ? rect.right - width : rect.left;
    const below = rect.bottom + gap;
    const top = below + height > window.innerHeight ? rect.top - gap - height : below;
    panel.style.position = 'fixed';
    panel.style.left = `${Math.max(8, Math.min(left, window.innerWidth - width - 8))}px`;
    panel.style.top = `${Math.max(8, top)}px`;
  }

  protected override updated(): void {
    if (!this.fixed) return;
    if (this.open) {
      this.placePanel();
      this.watchViewport();
    } else {
      this.unwatchViewport();
    }
  }

  /** Close from the outside, e.g. after a selection that ends the interaction. */
  close(): void {
    this.open = false;
  }

  override render(): TemplateResult {
    return html`
      <button
        class="dash-btn dash-dd-trigger ${this.triggerClass} ${this.open ? 'is-active' : ''}"
        type="button"
        aria-haspopup=${this.panelRole}
        title=${this.triggerTitle || nothing}
        aria-label=${this.triggerTitle || nothing}
        aria-expanded=${this.open ? 'true' : 'false'}
        @click=${() => {
          this.open = !this.open;
        }}
      >
        ${this.triggerContent ??
        html`${this.caption ? html`<span class="dash-dd-caption">${this.caption}</span>` : nothing}
          <span class="dash-dd-label">${this.label}</span>
          <span class="dash-dd-caret" aria-hidden="true">▾</span>`}
      </button>
      ${this.open
        ? html`<div
            class="dash-dd-panel ${this.panelClass} ${this.alignEnd ? 'is-end' : ''}"
            role=${this.panelRole}
          >
            ${this.panel()}
          </div>`
        : nothing}
    `;
  }
}
